import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Edit, Eye, Trash2 } from "react-feather";
import { FaTh, FaList } from "react-icons/fa";
import DataTable from "react-data-table-component";
import SellerNavbar from "../../component/SellerNavbar";
import SellerSidebar from "../../component/SellerSidebar";
import axios from "axios";
import Swal from "sweetalert2";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { baseurl, imageUrl } from "../../Baseurl";

const SellerAllListing = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");

  const [searchText, setSearchText] = useState("");
  const [conditionFilter, setConditionFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [currencyFilter, setCurrencyFilter] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage, setRecordsPerPage] = useState(12);

  useEffect(() => {
    fetchListings();
  }, []);

  const fetchListings = async () => {
    const sellerId = localStorage.getItem("seller_id");
    if (!sellerId) return;

    try {
      const response = await axios.get(`${baseurl}fetchSellerListingById/${sellerId}`);
      if (response.data?.data) {
        setListings(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching listings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteListing = async (listingId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will permanently delete your listing.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(`${baseurl}deleteListingById/${listingId}`);
          Swal.fire("Deleted!", "Your listing has been deleted.", "success");
          fetchListings();
        } catch {
          Swal.fire("Error!", "Failed to delete listing.", "error");
        }
      }
    });
  };

  const filteredListings = listings.filter(
    (listing) =>
      listing.title?.toLowerCase().includes(searchText.toLowerCase()) &&
      (!conditionFilter || listing.condition === conditionFilter) &&
      (!typeFilter || listing.type === typeFilter) &&
      (!currencyFilter || listing.currency === currencyFilter)
  );

  const uniqueOptions = (key) => [...new Set(listings.map((i) => i[key]).filter(Boolean))];

  const imageExtensions = [".jpg", ".jpeg", ".png", ".gif", ".webp"];

  const indexOfLast = currentPage * recordsPerPage;
  const currentRecords = filteredListings.slice(indexOfLast - recordsPerPage, indexOfLast);

  const renderSkeletonGrid = () => (
    <div className="row g-3">
      {Array.from({ length: 8 }).map((_, i) => (
        <div key={i} className="col-md-3">
          <div className="card listing-card p-2">
            <Skeleton height={180} />
            <Skeleton height={20} width="80%" className="mt-2" />
            <Skeleton height={15} width="60%" />
            <Skeleton height={30} width="40%" />
          </div>
        </div>
      ))}
    </div>
  );


  const renderGridPagination = () => {
  const totalPages = Math.ceil(filteredListings.length / recordsPerPage);
  if (totalPages <= 1) return null;

  const pageNumbers = [];
  const maxVisiblePages = 10;
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = startPage + maxVisiblePages - 1;

  if (endPage > totalPages) {
    endPage = totalPages;
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }

  for (let i = startPage; i <= endPage; i++) {
    pageNumbers.push(i);
  }

  return (
    <nav className="mt-3">
      <ul className="pagination justify-content-center">
        {/* Previous button */}
        <li
          className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
          onClick={() => currentPage > 1 && setCurrentPage(currentPage - 1)}
        >
          <button className="page-link">&laquo;</button>
        </li>

        {/* First page + ellipsis */}
        {startPage > 1 && (
          <>
            <li className="page-item" onClick={() => setCurrentPage(1)}>
              <button className="page-link">1</button>
            </li>
            {startPage > 2 && <li className="page-item disabled"><span className="page-link">...</span></li>}
          </>
        )}

        {/* Visible pages */}
        {pageNumbers.map((page) => (
          <li
            key={page}
            className={`page-item ${currentPage === page ? "active" : ""}`}
            onClick={() => setCurrentPage(page)}
          >
            <button className="page-link">{page}</button>
          </li>
        ))}

        {/* Last page + ellipsis */}
        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <li className="page-item disabled"><span className="page-link">...</span></li>}
            <li className="page-item" onClick={() => setCurrentPage(totalPages)}>
              <button className="page-link">{totalPages}</button>
            </li>
          </>
        )}

        {/* Next button */}
        <li
          className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}
          onClick={() => currentPage < totalPages && setCurrentPage(currentPage + 1)}
        >
          <button className="page-link">&raquo;</button>
        </li>
      </ul>
    </nav>
  );
};




  const renderGridView = () => (
  <>
    <div className="row g-3">
      {currentRecords.map((listing) => {
        const imageFile = listing.listing_images.find((img) =>
          imageExtensions.some((ext) => img.toLowerCase().endsWith(ext))
        );
        return (
          <div key={listing.listing_unique_id} className="col-md-3">
            <div className="card listing-card shadow-sm">
              <div className="listing-img-wrapper">
                <img
                  src={imageFile ? `${imageUrl}listing/${imageFile}` : "/placeholder.jpg"}
                  className="listing-img"
                  alt="Listing"
                />
              </div>
              <div className="p-3">
                <h6 className="listing-title">{listing.title}</h6>
                <p className="text-muted small mb-1">{listing.condition}</p>
                <p className="fw-bold text-primary mb-2">
                  {listing.currency} {listing.price}
                </p>
                <div className="d-flex justify-content-between">
                  <Link to={`/product/${listing.slug}`} target="_blank" className="btn btn-outline-primary btn-sm">
                    <Eye size={15} /> View
                  </Link>
                  <Link to={`/seller/edit-listing/${listing.listing_unique_id}`} className="btn btn-outline-warning btn-sm">
                    <Edit size={15} /> Edit
                  </Link>
                  <button
                    onClick={() => handleDeleteListing(listing.listing_unique_id)}
                    className="btn btn-outline-danger btn-sm"
                  >
                    <Trash2 size={15} /> Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>

    {/* Pagination for Grid View */}
    {renderGridPagination()}
  </>
);


  return (
    <>
      <SellerNavbar />
      <section className="seller-listing-section">
        <div className="container-fluid">
          <div className="row g-3">
            <div className="col-md-2">
              <SellerSidebar />
            </div>
            <div className="col-md-10">
              <div className="seller-listing-header">
                <h4>🛍️ My Listings</h4>
                <div className="view-toggle">
                  <button
                    className={`btn ${viewMode === "grid" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setViewMode("grid")}
                  >
                    <FaTh /> Grid
                  </button>
                  <button
                    className={`btn ${viewMode === "list" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setViewMode("list")}
                  >
                    <FaList /> List
                  </button>
                </div>
              </div>

              {/* Filters */}
              <div className="filter-bar shadow-sm p-3 mb-3 rounded">
                <div className="row g-3">
                  <div className="col-md-3">
                     <input
                  type="text"
                  className="form-control"
                  placeholder="🔍 Search by Title"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
                  </div>
                  <div className="col-md-3">
                    <select value={conditionFilter} onChange={(e) => setConditionFilter(e.target.value)} className="form-select">
                  <option value="">All Conditions</option>
                  {uniqueOptions("condition").map((v) => (
                    <option key={v}>{v}</option>
                  ))}
                </select>
                  </div>
                  <div className="col-md-3">
                    <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="form-select">
                  <option value="">All Types</option>
                  {uniqueOptions("type").map((v) => (
                    <option key={v}>{v}</option>
                  ))}
                </select>
                  </div>
                  <div className="col-md-3">
                    <select value={currencyFilter} onChange={(e) => setCurrencyFilter(e.target.value)} className="form-select">
                  <option value="">All Currencies</option>
                  {uniqueOptions("currency").map((v) => (
                    <option key={v}>{v}</option>
                  ))}
                </select>
                  </div>
                </div>
               
                
                
                
              </div>

              {loading ? (
                renderSkeletonGrid()
              ) : filteredListings.length === 0 ? (
                <div className="text-center mt-5">
                  <h5>No listings found 🫤</h5>
                </div>
              ) : viewMode === "grid" ? (
                renderGridView()
              ) : (
                <DataTable
                  title="Seller Listings"
                  columns={[
                    { name: "Title", selector: (row) => row.title },
                    { name: "Condition", selector: (row) => row.condition },
                    { name: "Type", selector: (row) => row.type },
                    { name: "Price", selector: (row) => `${row.currency} ${row.price}` },
                    {
                      name: "Action",
                      cell: (row) => (
                        <>
                          <Link
                            to={`/seller/edit-listing/${row.listing_unique_id}`}
                            className="btn btn-sm btn-outline-warning me-2"
                          >
                            <Edit size={14} /> Edit
                          </Link>
                          <button
                            onClick={() => handleDeleteListing(row.listing_unique_id)}
                            className="btn btn-sm btn-outline-danger"
                          >
                            <Trash2 size={14} /> Delete
                          </button>
                        </>
                      ),
                    },
                  ]}
                  data={filteredListings}
                  pagination
                />
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default SellerAllListing;
